import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mic, MicOff, ArrowLeft, RotateCcw, Check, User, Phone, MapPin, Users, Calendar, Stethoscope, CreditCard, Wallet, Printer, X, TimerReset, PhoneCall, Lock, KeyRound, Mail, LogIn, LogOut } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";

/**
 * Vyomra HealthOS — Self-Service Kiosk
 *
 * Full-screen, touch-friendly patient OPD registration flow. No auth wrapper:
 * the physical kiosk device serves this URL directly. All backend calls hit
 * unauthenticated `/api/kiosk/*` endpoints which hard-gate on the enabled flag.
 *
 * Flow: welcome → language → name → confirm-name → age → gender → mobile →
 *       (auto-search existing) → address → relative name → relative mobile →
 *       doctor pick → confirm charge → payment mode → API register → done.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api/kiosk`;
const AUTH_LOGIN = `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`;

// ─── i18n ────────────────────────────────────────────────────────────────
const T = {
  "en-IN": {
    welcome_title: "Welcome to Divine Hospital",
    welcome_sub: "Self-Service OPD Registration",
    tap_start: "Tap anywhere to begin",
    lang_title: "Select your language",
    lang_en: "English",
    lang_gu: "ગુજરાતી",
    name_prompt: "Please tell us your patient's name",
    age_prompt: "Please tell us the age",
    gender_prompt: "Select gender",
    mobile_prompt: "Please enter the mobile number",
    address_prompt: "Please tell us your address",
    rel_name_prompt: "Attendant / Relative name",
    rel_mobile_prompt: "Attendant mobile number",
    confirm_prompt: "Is this correct?",
    speak: "Speak",
    stop: "Stop",
    type: "Type",
    back: "Back",
    retry: "Retry",
    next: "Next",
    confirm: "Confirm",
    skip: "Skip",
    male: "Male", female: "Female", other: "Other",
    existing_found: "Existing patient found",
    existing_sub: "We found your record. Please confirm:",
    use_this: "Use this patient",
    new_patient: "Register as new patient",
    doctor_title: "Choose a doctor",
    dept_title: "Choose a department",
    charge_title: "Consultation Charge",
    proceed_pay: "Proceed to payment",
    pay_title: "Choose payment mode",
    processing: "Processing…",
    success: "Registration Successful",
    token: "Token",
    print_receipt: "Print OPD Receipt",
    print_token: "Print Token",
    new_patient_btn: "Start New Patient",
    start_over: "Start Over",
    disabled_title: "Kiosk Unavailable",
    disabled_sub: "Please contact reception for OPD registration.",
    session_warn: "Session will reset shortly due to inactivity.",
    mandatory: "Required",
    optional: "Optional",
    step: "Step",
    of: "of",
    // voice
    voice_prompts: {
      name: "Please tell us your name",
      age: "Please tell us your age",
      mobile: "Please enter your mobile number",
      address: "Please tell us your address",
    },
  },
  "gu-IN": {
    welcome_title: "ડિવાઇન હોસ્પિટલમાં આપનું સ્વાગત છે",
    welcome_sub: "સ્વ-સેવા OPD નોંધણી",
    tap_start: "શરૂ કરવા માટે ક્યાંય પણ ટેપ કરો",
    lang_title: "તમારી ભાષા પસંદ કરો",
    lang_en: "English",
    lang_gu: "ગુજરાતી",
    name_prompt: "કૃપા કરી દર્દીનું નામ જણાવો",
    age_prompt: "કૃપા કરી ઉંમર જણાવો",
    gender_prompt: "લિંગ પસંદ કરો",
    mobile_prompt: "કૃપા કરી મોબાઇલ નંબર દાખલ કરો",
    address_prompt: "કૃપા કરી તમારું સરનામું જણાવો",
    rel_name_prompt: "સંભાળ રાખનાર / સંબંધીનું નામ",
    rel_mobile_prompt: "સંભાળ રાખનારનો મોબાઇલ નંબર",
    confirm_prompt: "શું આ સાચું છે?",
    speak: "બોલો",
    stop: "બંધ કરો",
    type: "લખો",
    back: "પાછળ",
    retry: "ફરીથી",
    next: "આગળ",
    confirm: "પુષ્ટિ કરો",
    skip: "છોડો",
    male: "પુરુષ", female: "સ્ત્રી", other: "અન્ય",
    existing_found: "તમારો રેકોર્ડ મળ્યો",
    existing_sub: "કૃપા કરી પુષ્ટિ કરો:",
    use_this: "આ દર્દી પસંદ કરો",
    new_patient: "નવા દર્દી તરીકે નોંધણી કરો",
    doctor_title: "ડોક્ટર પસંદ કરો",
    dept_title: "વિભાગ પસંદ કરો",
    charge_title: "કન્સલ્ટેશન ચાર્જ",
    proceed_pay: "ચૂકવણી પર જાઓ",
    pay_title: "ચૂકવણી પદ્ધતિ પસંદ કરો",
    processing: "પ્રક્રિયા…",
    success: "નોંધણી સફળ થઈ",
    token: "ટોકન",
    print_receipt: "OPD રસીદ છાપો",
    print_token: "ટોકન છાપો",
    new_patient_btn: "નવો દર્દી શરૂ કરો",
    start_over: "ફરી શરૂ કરો",
    disabled_title: "કિઓસ્ક ઉપલબ્ધ નથી",
    disabled_sub: "OPD નોંધણી માટે કૃપા કરી રિસેપ્શનનો સંપર્ક કરો.",
    session_warn: "નિષ્ક્રિયતાને લીધે સેશન ટૂંક સમયમાં રીસેટ થશે.",
    mandatory: "જરૂરી",
    optional: "વૈકલ્પિક",
    step: "પગલું",
    of: "of",
    voice_prompts: {
      name: "તમારું નામ જણાવો",
      age: "તમારી ઉંમર જણાવો",
      mobile: "તમારો મોબાઇલ નંબર જણાવો",
      address: "તમારું સરનામું જણાવો",
    },
  },
};

// ─── digit-word helper (mirrors VoiceInput.speechToDigits, kept local) ───
const WD = { zero:"0", oh:"0", shunya:"0", one:"1", ek:"1", two:"2", do:"2", be:"2", three:"3", teen:"3", tran:"3", four:"4", char:"4", chaar:"4", five:"5", panch:"5", paanch:"5", six:"6", chhe:"6", chha:"6", seven:"7", saat:"7", eight:"8", aath:"8", nine:"9", nau:"9", nav:"9" };
const TE = { ten:"10", eleven:"11", twelve:"12", thirteen:"13", fourteen:"14", fifteen:"15", sixteen:"16", seventeen:"17", eighteen:"18", nineteen:"19" };
const TN = { twenty:"20", thirty:"30", forty:"40", fifty:"50", sixty:"60", seventy:"70", eighty:"80", ninety:"90" };
function speechToDigits(text) {
  if (!text) return "";
  const tokens = String(text).toLowerCase().replace(/[.,\-_/]/g, " ").split(/\s+/).filter(Boolean);
  let out = ""; let mult = 1;
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (t === "double") { mult = 2; continue; }
    if (t === "triple") { mult = 3; continue; }
    const raw = t.match(/\d+/g);
    if (raw) { out += raw.join("").repeat(mult); mult = 1; continue; }
    if (WD[t] !== undefined) { out += WD[t].repeat(mult); mult = 1; continue; }
    if (TE[t] !== undefined) { out += TE[t].repeat(mult); mult = 1; continue; }
    if (TN[t] !== undefined) {
      const next = tokens[i + 1];
      if (next && WD[next] !== undefined) { out += (parseInt(TN[t],10)+parseInt(WD[next],10)).toString(); i++; }
      else { out += TN[t]; }
      mult = 1; continue;
    }
    mult = 1;
  }
  return out;
}

// ─── Voice recognition hook (single utterance, per-step) ─────────────────
function useVoiceOnce(lang) {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const recogRef = useRef(null);

  const supported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const start = useCallback(() => {
    if (!supported) { toast.error("Voice not supported on this device"); return; }
    setTranscript("");
    const R = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new R();
    r.lang = lang || "en-IN";
    r.interimResults = true;
    r.maxAlternatives = 1;
    r.continuous = false;
    r.onresult = (e) => {
      let final = ""; let intr = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        if (res.isFinal) final += res[0].transcript;
        else intr += res[0].transcript;
      }
      setTranscript((prev) => (prev + " " + (final || intr)).trim());
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recogRef.current = r;
    setListening(true);
    r.start();
  }, [lang, supported]);

  const stop = useCallback(() => { try { recogRef.current?.stop(); } catch { /* browser stopped stream already */ } setListening(false); }, []);
  return { listening, transcript, setTranscript, start, stop, supported };
}

// ─── Reusable step chrome ────────────────────────────────────────────────
function StepFrame({ children, title, subtitle, progress, onBack, onStartOver, onLogout, tRef, cfg, operator }) {
  const hospitalName = cfg?.hospital_name || "Divine Hospital";
  const hospitalAddr = cfg?.hospital_address || "";
  const logoSrc = cfg?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${cfg.logo_url}` : LOGO_URL;
  return (
    <div className="min-h-screen w-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex flex-col">
      <div className="px-6 pt-4 pb-2 flex items-center justify-between border-b border-slate-200 bg-white/60 backdrop-blur">
        <div className="flex items-center gap-3">
          <img src={logoSrc} alt={hospitalName} className="h-12 object-contain"/>
          <div>
            <div className="text-[11px] tracking-widest text-slate-500 font-semibold uppercase">Self-Service Kiosk</div>
            {hospitalAddr && <div className="text-[10px] text-slate-400 flex items-center gap-1"><MapPin className="w-2.5 h-2.5"/>{hospitalAddr}</div>}
          </div>
        </div>
        {operator && (
          <div className="hidden md:flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-3 py-1 text-xs" data-testid="kiosk-operator-badge">
            <KeyRound className="w-3 h-3 text-blue-700"/>
            <span className="text-slate-500">Attended by</span>
            <span className="font-semibold text-blue-800">{operator.name}</span>
            {onLogout && (
              <button
                data-testid="kiosk-header-logout"
                onClick={onLogout}
                className="ml-1 text-red-600 hover:text-red-800 flex items-center gap-1 border-l border-blue-200 pl-2"
                title="Sign out"
              >
                <LogOut className="w-3 h-3"/> Logout
              </button>
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          {progress && <div className="text-xs text-slate-500 font-mono">{tRef.step} {progress.i} {tRef.of} {progress.total}</div>}
          {onBack && (
            <button data-testid="kiosk-back" onClick={onBack} className="px-4 py-2 rounded-lg border-2 border-slate-300 text-slate-700 text-sm hover:bg-slate-100 flex items-center gap-1">
              <ArrowLeft className="w-4 h-4"/> {tRef.back}
            </button>
          )}
          {onStartOver && (
            <button data-testid="kiosk-startover" onClick={onStartOver} className="px-4 py-2 rounded-lg border-2 border-red-300 text-red-700 text-sm hover:bg-red-50 flex items-center gap-1">
              <RotateCcw className="w-4 h-4"/> {tRef.start_over}
            </button>
          )}
          {onLogout && operator && (
            <button data-testid="kiosk-logout" onClick={onLogout} className="md:hidden px-4 py-2 rounded-lg border-2 border-red-300 text-red-700 text-sm hover:bg-red-50 flex items-center gap-1">
              <LogOut className="w-4 h-4"/> Logout
            </button>
          )}
        </div>
      </div>
      {progress && (
        <div className="h-1.5 bg-slate-100">
          <div className="h-full bg-blue-600 transition-all" style={{ width: `${(progress.i / progress.total) * 100}%` }}/>
        </div>
      )}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        <div className="w-full max-w-3xl">
          {title && <h1 className="text-4xl sm:text-5xl font-display font-semibold text-slate-900 text-center leading-tight">{title}</h1>}
          {subtitle && <p className="text-lg text-slate-500 text-center mt-2">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Voice + Type field step ─────────────────────────────────────────────
function VoiceTypeStep({ tRef, cfg, operator, lang, promptKey, value, onChange, onNext, numeric, maxLength, required = true, onBack, onStartOver, onLogout, progress, testid }) {
  const { listening, transcript, setTranscript, start, stop, supported } = useVoiceOnce(lang);
  const inputRef = useRef(null);

  useEffect(() => {
    if (transcript) {
      const clean = numeric ? speechToDigits(transcript) : transcript.trim();
      if (numeric) {
        const merged = (String(value || "").replace(/\D/g, "") + clean);
        onChange(maxLength ? merged.slice(0, maxLength) : merged);
      } else {
        onChange(clean);
      }
      setTranscript("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript]);

  const canProceed = required ? String(value || "").trim().length > 0 : true;
  if (numeric && maxLength && required) {
    // For phone we still want a canProceed on 10 digits
  }
  const promptText = tRef[promptKey];

  return (
    <StepFrame cfg={cfg} operator={operator} tRef={tRef} title={promptText} subtitle={required ? tRef.mandatory : tRef.optional} progress={progress} onBack={onBack} onStartOver={onStartOver} onLogout={onLogout}>
      <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-lg">
        <input
          ref={inputRef}
          data-testid={testid}
          value={value || ""}
          onChange={(e) => {
            let v = e.target.value;
            if (numeric) v = v.replace(/\D/g, "");
            if (maxLength && v.length > maxLength) v = v.slice(0, maxLength);
            onChange(v);
          }}
          inputMode={numeric ? "numeric" : "text"}
          maxLength={maxLength}
          autoFocus
          className="w-full text-3xl sm:text-4xl border-2 border-slate-300 rounded-xl px-6 py-4 focus:border-blue-600 focus:outline-none text-center"
        />
        <div className="mt-5 flex flex-wrap gap-3 justify-center">
          {supported && (
            <button
              data-testid={`${testid}-mic`}
              onClick={listening ? stop : start}
              className={`px-8 py-4 rounded-xl text-lg font-semibold flex items-center gap-2 border-2 transition ${listening ? "bg-red-600 text-white border-red-700 animate-pulse" : "bg-white text-blue-700 border-blue-300 hover:bg-blue-50"}`}>
              {listening ? <MicOff className="w-5 h-5"/> : <Mic className="w-5 h-5"/>}
              {listening ? tRef.stop : tRef.speak}
            </button>
          )}
          {!required && (
            <button data-testid={`${testid}-skip`} onClick={() => onNext(true)} className="px-8 py-4 rounded-xl text-lg font-semibold border-2 border-slate-300 text-slate-600 hover:bg-slate-100">
              {tRef.skip}
            </button>
          )}
          <button
            data-testid={`${testid}-next`}
            onClick={() => onNext(false)}
            disabled={!canProceed}
            className={`px-8 py-4 rounded-xl text-lg font-semibold border-2 transition ${canProceed ? "bg-blue-700 text-white border-blue-800 hover:bg-blue-800" : "bg-slate-200 text-slate-400 border-slate-300 cursor-not-allowed"}`}>
            {tRef.next} →
          </button>
        </div>
      </div>
    </StepFrame>
  );
}

// ─── Gender pick ─────────────────────────────────────────────────────────
function GenderStep({ tRef, cfg, operator, value, onChange, onNext, onBack, onStartOver, onLogout, progress }) {
  const opts = [
    { k: "Male",   label: tRef.male   },
    { k: "Female", label: tRef.female },
    { k: "Other",  label: tRef.other  },
  ];
  return (
    <StepFrame cfg={cfg} operator={operator} tRef={tRef} title={tRef.gender_prompt} subtitle={tRef.optional} progress={progress} onBack={onBack} onStartOver={onStartOver} onLogout={onLogout}>
      <div className="grid grid-cols-3 gap-4">
        {opts.map((o) => (
          <button key={o.k} data-testid={`kiosk-gender-${o.k}`} onClick={() => { onChange(o.k); onNext(); }}
            className={`p-8 rounded-2xl border-2 text-2xl font-semibold transition ${value === o.k ? "bg-blue-700 text-white border-blue-800" : "bg-white text-slate-700 border-slate-300 hover:border-blue-500 hover:bg-blue-50"}`}>
            {o.label}
          </button>
        ))}
      </div>
      <div className="flex justify-center gap-3 mt-6">
        <button data-testid="kiosk-gender-skip" onClick={onNext} className="px-8 py-3 rounded-xl border-2 border-slate-300 text-slate-600">{tRef.skip}</button>
      </div>
    </StepFrame>
  );
}

// ─── Existing patient found ──────────────────────────────────────────────
function ExistingPatientStep({ tRef, cfg, operator, matches, onSelect, onNew, onBack, onStartOver, onLogout }) {
  return (
    <StepFrame cfg={cfg} operator={operator} tRef={tRef} title={tRef.existing_found} subtitle={tRef.existing_sub} onBack={onBack} onStartOver={onStartOver} onLogout={onLogout}>
      <div className="space-y-3">
        {matches.map((m) => (
          <button key={m.id} data-testid={`kiosk-existing-${m.id}`} onClick={() => onSelect(m)}
            className="w-full text-left bg-white border-2 border-slate-300 rounded-xl p-5 hover:border-blue-600 hover:bg-blue-50 transition">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-semibold text-slate-900">{m.name}</div>
                <div className="text-sm text-slate-500 font-mono">UHID · {m.uhid}</div>
                <div className="text-sm text-slate-600 mt-1">{m.age} · {m.gender} · {m.mobile}</div>
              </div>
              <div className="text-blue-700 text-sm font-semibold flex items-center gap-1"><Check className="w-4 h-4"/> {tRef.use_this}</div>
            </div>
          </button>
        ))}
        <button data-testid="kiosk-existing-new" onClick={onNew} className="w-full py-4 rounded-xl border-2 border-dashed border-slate-300 text-slate-600 hover:border-blue-500 hover:bg-blue-50 text-lg font-medium">
          {tRef.new_patient}
        </button>
      </div>
    </StepFrame>
  );
}

// ─── Doctor pick (grouped by department) ─────────────────────────────────
function DoctorStep({ tRef, cfg, operator, doctors, selected, onSelect, onNext, onBack, onStartOver, onLogout }) {
  const [dept, setDept] = useState("");
  const departments = useMemo(() => Array.from(new Set(doctors.map((d) => d.department))).filter(Boolean).sort(), [doctors]);
  const filtered = useMemo(() => dept ? doctors.filter((d) => d.department === dept) : doctors, [dept, doctors]);
  return (
    <StepFrame cfg={cfg} operator={operator} tRef={tRef} title={dept ? tRef.doctor_title : tRef.dept_title} onBack={onBack} onStartOver={onStartOver} onLogout={onLogout}>
      {!dept ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {departments.map((d) => (
            <button key={d} data-testid={`kiosk-dept-${d}`} onClick={() => setDept(d)}
              className="p-6 bg-white rounded-2xl border-2 border-slate-300 hover:border-blue-600 hover:bg-blue-50 text-xl font-semibold text-slate-800">
              {d}
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3 justify-center">
            <button onClick={() => setDept("")} className="text-sm text-blue-700 hover:underline flex items-center gap-1"><ArrowLeft className="w-3 h-3"/> {tRef.back}</button>
            <span className="text-xs text-slate-500">·</span>
            <span className="text-sm font-semibold text-slate-700">{dept}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[420px] overflow-y-auto">
            {filtered.map((doc) => (
              <button key={doc.id} data-testid={`kiosk-doc-${doc.id}`} onClick={() => { onSelect(doc); onNext(); }}
                className={`flex items-center gap-3 p-4 rounded-2xl border-2 text-left transition ${selected?.id === doc.id ? "border-blue-700 bg-blue-50" : "border-slate-300 bg-white hover:border-blue-500 hover:bg-blue-50"}`}>
                <div className="w-14 h-14 rounded-full bg-slate-100 grid place-items-center overflow-hidden">
                  {doc.photo ? <img src={doc.photo} alt={doc.name} className="w-full h-full object-cover"/> : <Stethoscope className="w-7 h-7 text-slate-400"/>}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-lg text-slate-900">{doc.name}</div>
                  <div className="text-xs text-slate-500">{doc.qualification}</div>
                  <div className="text-sm text-blue-700 font-mono mt-1">₹{doc.opd_charge}</div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </StepFrame>
  );
}

// ─── Charge & payment ────────────────────────────────────────────────────
function ChargeStep({ tRef, cfg, operator, doctor, chargeAmount, onChangeAmount, onNext, onBack, onStartOver, onLogout }) {
  const [reason, setReason] = useState("");
  const defaultFee = Number(doctor.opd_charge) || 0;
  // Show chargeAmount as-is (including empty string so the operator can fully
  // clear the field and type a new number without any bounce-back).
  const raw = chargeAmount == null ? defaultFee : chargeAmount;
  const parsed = raw === "" || raw === null ? NaN : Number(raw);
  const effective = Number.isNaN(parsed) ? defaultFee : parsed;   // used on Proceed
  const changed = !Number.isNaN(parsed) && Math.abs(parsed - defaultFee) > 0.001;
  const invalid = raw !== "" && (Number.isNaN(parsed) || parsed < 0);
  const disabled = invalid || (changed && !reason.trim());
  return (
    <StepFrame cfg={cfg} operator={operator} tRef={tRef} title={tRef.charge_title} onBack={onBack} onStartOver={onStartOver} onLogout={onLogout}>
      <div className="bg-white rounded-3xl border-2 border-blue-200 p-8 text-center shadow-lg">
        <div className="text-slate-500 text-sm uppercase tracking-widest">{doctor.department}</div>
        <div className="text-3xl font-semibold mt-1">{doctor.name}</div>
        <div className="text-xs text-slate-500 mt-1">{doctor.qualification}</div>
        <div className="mt-6 py-6 border-y border-slate-200">
          <div className="text-slate-500 text-sm uppercase tracking-widest">OPD Consultation</div>
          <div className="mt-3 flex items-baseline justify-center gap-2">
            <span className="text-5xl font-mono font-bold text-blue-800">₹</span>
            <input
              data-testid="kiosk-charge-amount"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={raw}
              placeholder="0"
              onFocus={(e) => e.target.select()}
              onChange={(e) => {
                // Allow empty string + digits only; strip anything else so the
                // operator can clear the field and retype freely.
                const clean = e.target.value.replace(/[^0-9]/g, "");
                onChangeAmount(clean);
              }}
              className={`text-6xl font-mono font-bold text-center bg-transparent border-b-2 outline-none w-56 ${invalid ? "text-red-600 border-red-400" : "text-blue-800 border-blue-200 focus:border-blue-600"}`}
            />
          </div>
          {raw === "" && (
            <div className="text-[11px] text-slate-500 mt-2">Leave empty to use default ₹{defaultFee}</div>
          )}
          {changed && (
            <div className="mt-4 max-w-md mx-auto text-left">
              <div className="text-xs text-slate-500 mb-1 flex justify-between">
                <span>Default price: <b className="font-mono">₹{defaultFee}</b></span>
                <button
                  data-testid="kiosk-charge-reset"
                  onClick={() => { onChangeAmount(defaultFee); setReason(""); }}
                  className="text-blue-700 hover:underline"
                >
                  Reset
                </button>
              </div>
              <label className="block text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                Reason for change <span className="text-red-600">*</span>
              </label>
              <input
                data-testid="kiosk-charge-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Senior citizen discount, staff family, follow-up waiver…"
                className="w-full mt-1 border-2 border-slate-300 focus:border-blue-600 rounded-xl px-3 py-2 text-sm outline-none"
              />
            </div>
          )}
        </div>
        <button
          data-testid="kiosk-proceed-pay"
          onClick={() => onNext(effective, reason.trim())}
          disabled={disabled}
          className={`mt-6 w-full py-5 text-white text-xl font-semibold rounded-xl ${disabled ? "bg-slate-300 cursor-not-allowed" : "bg-blue-700 hover:bg-blue-800"}`}
        >
          {tRef.proceed_pay}
        </button>
      </div>
    </StepFrame>
  );
}

function PaymentStep({ tRef, cfg, operator, allowedModes, onPick, onBack, onStartOver, onLogout }) {
  const icons = { Cash: Wallet, UPI: CreditCard, Credit: PhoneCall };
  return (
    <StepFrame cfg={cfg} operator={operator} tRef={tRef} title={tRef.pay_title} onBack={onBack} onStartOver={onStartOver} onLogout={onLogout}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {allowedModes.map((m) => {
          const Icon = icons[m] || Wallet;
          return (
            <button key={m} data-testid={`kiosk-pay-${m}`} onClick={() => onPick(m)}
              className="p-8 bg-white rounded-2xl border-2 border-slate-300 hover:border-blue-600 hover:bg-blue-50 text-2xl font-semibold text-slate-800 flex items-center justify-center gap-3">
              <Icon className="w-8 h-8"/> {m}
            </button>
          );
        })}
      </div>
    </StepFrame>
  );
}

// ─── Success ─────────────────────────────────────────────────────────────
function SuccessStep({ tRef, cfg, operator, result, onNew, onLogout }) {
  const layout = cfg?.printer || "thermal80";
  const hospitalName = cfg?.hospital_name || "Divine Multi-Speciality Hospital";
  const hospitalAddr = cfg?.hospital_address || "Jamnagar, Gujarat";
  const hospitalPhone = cfg?.hospital_phone || "";
  const openReceipt = () => window.open(`/bill/opd/${result.opd.id}/print?layout=${layout}`, "_blank");
  const queueNo = result.opd.opd_no;   // unified series — shared with Reception
  const openToken = () => {
    const w = window.open("", "_blank", "width=380,height=560");
    if (!w) return;
    // Build the token slip via DOM APIs so no user data ever touches innerHTML.
    // Every value goes through textContent → automatic HTML escaping.
    const d = w.document;
    d.title = `OPD ${queueNo}`;
    const style = d.createElement("style");
    style.textContent = `body{font-family:monospace;text-align:center;padding:20px;} h1{font-size:64px;margin:12px 0;letter-spacing:.05em;} .lbl{color:#666;letter-spacing:.2em;font-size:11px;text-transform:uppercase;} .addr{font-size:11px;color:#666;margin-top:4px;} hr.dash{margin:12px 0;border:0;border-top:1px dashed #999}`;
    d.head.appendChild(style);
    const line = (cls, text, tag = "div") => {
      const el = d.createElement(tag);
      if (cls) el.className = cls;
      el.textContent = text;
      d.body.appendChild(el);
      return el;
    };
    line("lbl", hospitalName);
    line("addr", `${hospitalAddr}${hospitalPhone ? " · " + hospitalPhone : ""}`);
    d.body.appendChild(Object.assign(d.createElement("hr"), { className: "dash" }));
    line("lbl", "OPD No.");
    line("", queueNo, "h1");
    line("", result.token.doctor);
    line("", result.token.department).setAttribute("style", "color:#666;font-size:12px");
    d.body.appendChild(Object.assign(d.createElement("hr"), { className: "dash" }));
    line("", result.patient.name).setAttribute("style", "font-size:14px");
    line("", `UHID · ${result.patient.uhid}`).setAttribute("style", "font-family:monospace;font-size:11px;color:#666");
    line("", `Bill · ${result.payment.receipt_no}`).setAttribute("style", "font-family:monospace;font-size:11px;color:#666");
    // Small platform footer at the very end of the token slip.
    const foot = d.createElement("div");
    foot.textContent = "POWERED BY THE VYOMRA HOS";
    foot.setAttribute("style", "font-size:7px;letter-spacing:0.14em;color:#94a3b8;margin-top:10px;");
    d.body.appendChild(foot);
    // Auto-print after paint (no inline <script> injection).
    w.addEventListener("load", () => setTimeout(() => w.print(), 300), { once: true });
    setTimeout(() => { try { w.print(); } catch { /* popup already closed by user */ } }, 500);
  };
  return (
    <StepFrame cfg={cfg} operator={operator} tRef={tRef} onLogout={onLogout}>
      <div className="bg-white rounded-3xl border-2 border-emerald-200 p-8 shadow-lg text-center">
        <div className="w-20 h-20 rounded-full bg-emerald-100 grid place-items-center mx-auto">
          <Check className="w-10 h-10 text-emerald-600"/>
        </div>
        <h1 className="text-4xl font-display font-semibold mt-4 text-slate-900">{tRef.success}</h1>
        <div className="mt-6 grid grid-cols-2 gap-4 text-left max-w-lg mx-auto">
          <Field lbl="Name"    val={result.patient.name}/>
          <Field lbl="UHID"    val={result.patient.uhid}   mono/>
          <Field lbl="Bill No" val={result.payment.receipt_no} mono/>
          <Field lbl="Doctor"  val={result.opd.doctor_name}/>
          <Field lbl="Dept"    val={result.opd.department}/>
          <Field lbl="Paid"    val={`₹${result.payment.amount} · ${result.payment.payment_mode}`}/>
        </div>
        <div className="mt-6">
          <div className="text-xs text-slate-500 uppercase tracking-widest">OPD No.</div>
          <div className="text-7xl font-mono font-bold text-blue-800" data-testid="kiosk-done-opdno">{queueNo}</div>
          {operator && (
            <div className="mt-2 text-xs text-slate-500">
              Attended by <b className="text-slate-800">{operator.name}</b> ({operator.role})
            </div>
          )}
        </div>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <button data-testid="kiosk-print-receipt" onClick={openReceipt} className="px-6 py-4 bg-blue-700 hover:bg-blue-800 text-white rounded-xl font-semibold flex items-center gap-2">
            <Printer className="w-5 h-5"/> {tRef.print_receipt}
          </button>
          <button data-testid="kiosk-print-token" onClick={openToken} className="px-6 py-4 bg-white border-2 border-blue-300 text-blue-700 rounded-xl font-semibold flex items-center gap-2 hover:bg-blue-50">
            <Printer className="w-5 h-5"/> {tRef.print_token}
          </button>
          <button data-testid="kiosk-new" onClick={onNew} className="px-6 py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold flex items-center gap-2">
            <User className="w-5 h-5"/> {tRef.new_patient_btn}
          </button>
        </div>
      </div>
    </StepFrame>
  );
}
function Field({ lbl, val, mono }) {
  return (
    <div className="bg-slate-50 rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{lbl}</div>
      <div className={`text-base font-medium text-slate-900 ${mono ? "font-mono" : ""}`}>{val}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  KIOSK STATE MACHINE
// ═══════════════════════════════════════════════════════════════════════════
const EMPTY = { name: "", age: "", gender: "Male", mobile: "", address: "", relative_name: "", relative_mobile: "" };
const STEPS_NEW = ["welcome","lang","name","age","gender","mobile","existing","address","rel_name","rel_mobile","doctor","charge","payment","done"];
const STEPS_EXISTING = ["welcome","lang","name","age","gender","mobile","existing","doctor","charge","payment","done"]; // rel/address skipped for existing
const PROGRESS_TOTAL = 11;

export default function Kiosk() {
  const [cfg, setCfg] = useState(null);
  const [step, setStep] = useState("welcome");
  const [lang, setLang] = useState("en-IN");
  const [form, setForm] = useState({ ...EMPTY });
  const [existingMatches, setExistingMatches] = useState([]);
  const [selectedExisting, setSelectedExisting] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [doctor, setDoctor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [operator, setOperator] = useState(null);   // { id, name, email, role, token }
  const [chargeAmount, setChargeAmount] = useState(null);   // operator-editable fee
  const [chargeReason, setChargeReason] = useState("");     // required when != default
  const [loginDraft, setLoginDraft] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const tRef = T[lang];
  const idleTimer = useRef(null);

  // Load config
  useEffect(() => {
    axios.get(`${API}/config/public`).then((r) => {
      setCfg(r.data);
      setLang(r.data.default_language || "en-IN");
    }).catch(() => setCfg({ enabled: false }));
  }, []);

  // Load doctors on demand
  useEffect(() => {
    if (step === "doctor" && doctors.length === 0) {
      axios.get(`${API}/doctors`).then((r) => setDoctors(r.data || [])).catch(() => toast.error("Could not load doctors"));
    }
  }, [step, doctors.length]);

  // Idle timer
  const armIdleReset = useCallback(() => {
    clearTimeout(idleTimer.current);
    const secs = cfg?.session_timeout_seconds || 120;
    if (step === "welcome") return;
    idleTimer.current = setTimeout(() => hardReset(true), secs * 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg, step]);
  useEffect(() => {
    const handler = () => armIdleReset();
    window.addEventListener("mousemove", handler);
    window.addEventListener("keydown", handler);
    window.addEventListener("touchstart", handler);
    armIdleReset();
    return () => {
      window.removeEventListener("mousemove", handler);
      window.removeEventListener("keydown", handler);
      window.removeEventListener("touchstart", handler);
      clearTimeout(idleTimer.current);
    };
  }, [armIdleReset]);

  const hardReset = (silent = false) => {
    // Reset the patient-facing form + result but PRESERVE the signed-in operator.
    // Same behavior for the idle timer, the "Start Over" header button and the
    // "Start New Patient" success-screen button — the operator stays signed in
    // until they explicitly tap Logout.
    setForm({ ...EMPTY });
    setExistingMatches([]);
    setSelectedExisting(null);
    setDoctor(null);
    setResult(null);
    setChargeAmount(null);
    setChargeReason("");
    if (operator) {
      // Skip login — go straight to language pick for the next patient.
      setStep("lang");
    } else {
      setLoginDraft({ email: "", password: "" });
      setLoginError("");
      setStep("welcome");
    }
    if (!silent) toast.success("Ready for next patient");
  };

  const logout = () => {
    setForm({ ...EMPTY });
    setExistingMatches([]);
    setSelectedExisting(null);
    setDoctor(null);
    setResult(null);
    setOperator(null);
    setChargeAmount(null);
    setChargeReason("");
    setLoginDraft({ email: "", password: "" });
    setLoginError("");
    setStep("welcome");
    toast.success("Signed out");
  };

  // Progress helper (fake but useful)
  const progress = useMemo(() => {
    const order = ["welcome","lang","name","age","gender","mobile","existing","address","rel_name","rel_mobile","doctor","charge","payment","done"];
    const i = Math.max(order.indexOf(step), 0);
    return { i: Math.min(i, PROGRESS_TOTAL), total: PROGRESS_TOTAL };
  }, [step]);

  const back = () => {
    const flow = selectedExisting ? STEPS_EXISTING : STEPS_NEW;
    const idx = flow.indexOf(step);
    if (idx > 0) setStep(flow[idx - 1]);
  };

  // ── Existing patient auto-search after mobile ────────────────────────
  const searchExisting = async () => {
    const q = (form.mobile || "").trim();
    if (q.length < 5) { setStep(selectedExisting ? "doctor" : "address"); return; }
    try {
      const { data } = await axios.post(`${API}/patient/search`, { query: q });
      if (data.length > 0) { setExistingMatches(data); setStep("existing"); }
      else { setStep("address"); }
    } catch { setStep("address"); }
  };

  // ── Register call ─────────────────────────────────────────────────────
  const submit = async (paymentMode) => {
    setBusy(true);
    try {
      const defaultFee = Number(doctor?.opd_charge) || 0;
      const finalAmt = chargeAmount == null || chargeAmount === "" ? defaultFee : Number(chargeAmount);
      const overrideBlock = Math.abs(finalAmt - defaultFee) > 0.001
        ? { override_amount: finalAmt, override_reason: chargeReason }
        : {};
      const base = selectedExisting
        ? { patient_id: selectedExisting.id, doctor_id: doctor.id, payment_mode: paymentMode }
        : { ...form, age: parseInt(form.age, 10) || 0, doctor_id: doctor.id, payment_mode: paymentMode };
      const payload = { ...base, ...overrideBlock };
      const { data } = await axios.post(`${API}/register`, payload, {
        headers: operator?.token ? { Authorization: `Bearer ${operator.token}` } : {},
      });
      setResult(data);
      setStep("done");
    } catch (e) {
      const msg = e.response?.data?.detail || "Registration failed";
      toast.error(msg);
      if (e.response?.status === 401) {
        // Session expired — bounce back to login.
        setOperator(null);
        setStep("login");
      }
    } finally { setBusy(false); }
  };

  // ── Render ────────────────────────────────────────────────────────────
  if (!cfg) return <div className="min-h-screen grid place-items-center text-lg">Loading…</div>;
  if (!cfg.enabled) {
    return (
      <StepFrame cfg={cfg} operator={operator} tRef={tRef} title={tRef.disabled_title} subtitle={tRef.disabled_sub}>
        <div className="text-center">
          <X className="w-16 h-16 text-red-500 mx-auto"/>
        </div>
      </StepFrame>
    );
  }

  if (step === "welcome") {
    const logoSrc = cfg?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${cfg.logo_url}` : LOGO_URL;
    const onTap = () => setStep(operator ? "lang" : "login");
    return (
      <div onClick={onTap} className="min-h-screen w-screen bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 text-white flex flex-col items-center justify-center cursor-pointer">
        <div className="text-center px-6" data-testid="kiosk-welcome">
          <div className="inline-flex bg-white rounded-2xl p-4 shadow-2xl">
            <img src={logoSrc} alt={cfg.hospital_name || "Hospital"} className="h-24 sm:h-32 object-contain"/>
          </div>
          <h1 className="text-4xl sm:text-6xl font-display font-semibold mt-8">{tRef.welcome_title}</h1>
          <p className="text-xl sm:text-2xl text-blue-100 mt-3">{tRef.welcome_sub}</p>
          {cfg.hospital_address && (
            <div className="mt-6 inline-flex items-center gap-2 text-blue-100 text-base bg-white/10 rounded-full px-4 py-1.5 backdrop-blur">
              <MapPin className="w-4 h-4"/> {cfg.hospital_address}
              {cfg.hospital_phone && <><span>·</span><Phone className="w-4 h-4"/> {cfg.hospital_phone}</>}
            </div>
          )}
          {operator && (
            <div className="mt-6 inline-flex items-center gap-2 bg-white/15 border border-white/30 rounded-full px-4 py-1.5 text-sm">
              <KeyRound className="w-3.5 h-3.5"/> Signed in as <b className="font-semibold">{operator.name}</b>
              <button
                data-testid="kiosk-welcome-logout"
                onClick={(e) => { e.stopPropagation(); logout(); }}
                className="ml-2 text-red-200 hover:text-white flex items-center gap-1 text-xs uppercase tracking-widest border-l border-white/30 pl-3"
              >
                <LogOut className="w-3.5 h-3.5"/> Logout
              </button>
            </div>
          )}
          <p className="text-lg text-blue-200 mt-16 animate-pulse">{tRef.tap_start}</p>
        </div>
      </div>
    );
  }

  if (step === "login") {
    const submitLogin = async () => {
      setLoginError("");
      const email = (loginDraft.email || "").trim().toLowerCase();
      const password = loginDraft.password || "";
      if (!email || !password) { setLoginError("Enter email and password"); return; }
      setLoginBusy(true);
      try {
        const { data } = await axios.post(AUTH_LOGIN, { email, password });
        setOperator({ id: data.id, name: data.name, email: data.email, role: data.role, token: data.token });
        setLoginDraft({ email: "", password: "" });
        setStep("lang");
      } catch (e) {
        const d = e.response?.data?.detail;
        const msg = Array.isArray(d) ? d.map((x) => x.msg).join(" · ") : (typeof d === "string" ? d : "Login failed");
        setLoginError(msg);
      } finally { setLoginBusy(false); }
    };
    const logoSrc = cfg?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${cfg.logo_url}` : LOGO_URL;
    return (
      <StepFrame cfg={cfg} operator={operator} tRef={tRef} title="Staff Sign-In" subtitle="Use your The Vyomra HOS email and password so every registration is attributed to you." onStartOver={hardReset}>
        <div className="max-w-md mx-auto bg-white border-2 border-slate-200 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-center mb-4">
            <img src={logoSrc} alt="" className="h-10 object-contain"/>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Email</span>
            <div className="mt-1 flex items-center gap-2 border-2 border-slate-300 rounded-xl px-3 focus-within:border-blue-600">
              <Mail className="w-5 h-5 text-slate-400"/>
              <input
                data-testid="kiosk-login-email"
                type="email"
                autoComplete="username"
                value={loginDraft.email}
                onChange={(e) => setLoginDraft((d) => ({ ...d, email: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") submitLogin(); }}
                className="flex-1 py-3 text-lg outline-none bg-transparent"
                placeholder="staff@divinehospital.in"
                autoFocus
              />
            </div>
          </label>
          <label className="block mt-4">
            <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Password</span>
            <div className="mt-1 flex items-center gap-2 border-2 border-slate-300 rounded-xl px-3 focus-within:border-blue-600">
              <Lock className="w-5 h-5 text-slate-400"/>
              <input
                data-testid="kiosk-login-password"
                type="password"
                autoComplete="current-password"
                value={loginDraft.password}
                onChange={(e) => setLoginDraft((d) => ({ ...d, password: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") submitLogin(); }}
                className="flex-1 py-3 text-lg outline-none bg-transparent"
                placeholder="••••••••"
              />
            </div>
          </label>
          {loginError && (
            <div className="mt-3 text-center text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg py-2" data-testid="kiosk-login-error">
              {loginError}
            </div>
          )}
          <button
            data-testid="kiosk-login-submit"
            onClick={submitLogin}
            disabled={loginBusy}
            className="mt-5 w-full py-4 rounded-xl bg-blue-700 hover:bg-blue-800 disabled:bg-slate-300 text-white text-lg font-semibold flex items-center justify-center gap-2">
            {loginBusy ? "Signing in…" : (<><LogIn className="w-5 h-5"/> Sign in & start</>)}
          </button>
          <div className="text-[11px] text-slate-500 mt-3 text-center">Same email/password you use for the main The Vyomra HOS app.</div>
        </div>
      </StepFrame>
    );
  }

  if (step === "lang") {
    return (
      <StepFrame cfg={cfg} operator={operator} tRef={tRef} title={tRef.lang_title} progress={progress} onStartOver={hardReset} onLogout={logout}>
        <div className="grid grid-cols-2 gap-5">
          <button data-testid="kiosk-lang-en" onClick={() => { setLang("en-IN"); setStep("name"); }} className="p-10 rounded-3xl bg-white border-2 border-slate-300 hover:border-blue-600 hover:bg-blue-50 text-4xl font-semibold text-slate-800">English</button>
          <button data-testid="kiosk-lang-gu" onClick={() => { setLang("gu-IN"); setStep("name"); }} className="p-10 rounded-3xl bg-white border-2 border-slate-300 hover:border-blue-600 hover:bg-blue-50 text-4xl font-semibold text-slate-800">ગુજરાતી</button>
        </div>
      </StepFrame>
    );
  }

  const common = { tRef, cfg, lang, onBack: back, onStartOver: hardReset, onLogout: logout, progress, operator };
  switch (step) {
    case "name":
      return <VoiceTypeStep {...common} promptKey="name_prompt" value={form.name} onChange={(v) => setForm({ ...form, name: v })} onNext={() => setStep("age")} testid="kiosk-name"/>;
    case "age":
      return <VoiceTypeStep {...common} promptKey="age_prompt" value={form.age} onChange={(v) => setForm({ ...form, age: v })} numeric maxLength={3} onNext={() => setStep("gender")} testid="kiosk-age"/>;
    case "gender":
      return <GenderStep {...common} value={form.gender} onChange={(v) => setForm({ ...form, gender: v })} onNext={() => setStep("mobile")}/>;
    case "mobile":
      return <VoiceTypeStep {...common} promptKey="mobile_prompt" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} numeric maxLength={10} onNext={searchExisting} testid="kiosk-mobile"/>;
    case "existing":
      return <ExistingPatientStep {...common} matches={existingMatches}
        onSelect={(m) => { setSelectedExisting(m); setForm({ ...form, name: m.name, age: m.age, gender: m.gender || "Male", address: m.address || "" }); setStep("doctor"); }}
        onNew={() => { setSelectedExisting(null); setStep("address"); }}/>;
    case "address":
      return <VoiceTypeStep {...common} promptKey="address_prompt" value={form.address} onChange={(v) => setForm({ ...form, address: v })} onNext={() => setStep("rel_name")} testid="kiosk-address"/>;
    case "rel_name":
      return <VoiceTypeStep {...common} promptKey="rel_name_prompt" value={form.relative_name} onChange={(v) => setForm({ ...form, relative_name: v })} required={false} onNext={() => setStep("rel_mobile")} testid="kiosk-relname"/>;
    case "rel_mobile":
      return <VoiceTypeStep {...common} promptKey="rel_mobile_prompt" value={form.relative_mobile} onChange={(v) => setForm({ ...form, relative_mobile: v })} numeric maxLength={10} required={false} onNext={() => setStep("doctor")} testid="kiosk-relmob"/>;
    case "doctor":
      return <DoctorStep {...common} doctors={doctors} selected={doctor} onSelect={setDoctor} onNext={() => setStep("charge")}/>;
    case "charge":
      return <ChargeStep {...common} doctor={doctor} chargeAmount={chargeAmount} onChangeAmount={setChargeAmount} onNext={(amt, reason) => { setChargeAmount(amt); setChargeReason(reason || ""); setStep("payment"); }}/>;
    case "payment":
      return busy
        ? <StepFrame cfg={cfg} operator={operator} tRef={tRef} title={tRef.processing} onLogout={logout}><div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"/></StepFrame>
        : <PaymentStep {...common} allowedModes={cfg.allowed_payment_methods} onPick={submit}/>;
    case "done":
      return <SuccessStep tRef={tRef} cfg={cfg} operator={operator} result={result} onNew={hardReset} onLogout={logout}/>;
    default:
      return null;
  }
}
